/**
 * Günlük iş planı ve günlük raporlar (ADR 0051): Operations ▸ Daily Works Plan ve Daily Reports.
 * Yazım BGM referansındaki gibi (`gemi-gunluk-is-plani.jpeg`).
 */
export const dailyEn = {
  departments: {
    deck: 'Deck',
    engine: 'Engine',
    navigation: 'Navigation',
    safety: 'Safety',
    environment: 'Environment',
    interior: 'Interior',
    admin: 'Admin',
    all: 'All',
  },
  priorities: { low: 'Low', medium: 'Medium', high: 'High' },
  statuses: {
    planned: 'Planned',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
    overdue: 'Overdue',
  },
  kinds: {
    noon: 'Noon Report',
    daily: 'Daily Report',
    port: 'Port Report',
    defect_observation: 'Defect / Observation',
    safety: 'Safety Report',
  },
  reportStatuses: {
    draft: 'Draft',
    submitted: 'Submitted',
    approved: 'Approved',
    final: 'Submitted',
    returned: 'Returned',
    reopened: 'Reopened',
    open: 'Open',
    closed: 'Closed',
  },
  weather: { good: 'Good', fair: 'Fair', moderate: 'Moderate', poor: 'Poor', bad: 'Bad' },
  sea: {
    calm: 'Calm',
    slight: 'Slight',
    moderate: 'Moderate',
    rough: 'Rough',
    very_rough: 'Very rough',
  },
  categories: { defect: 'Defect', observation: 'Observation' },
  results: { passed: 'Passed', partial: 'Partially passed', failed: 'Failed' },
  head: {
    region: 'Vessel and date',
    vesselType: 'Vessel Type',
    flag: 'Flag',
    date: 'Date',
    imo: 'IMO {{imo}}',
    vessel: 'Vessel',
  },
  plan: {
    title: 'Daily Works Plan',
    description: 'Plan the daily works and assign responsibilities.',
    descriptionOffice: 'Daily works are planned on board. The office sees the plan of each day.',
    all: 'All',
    tabs: 'Departments',
    search: 'Search task...',
    searchLabel: 'Search tasks',
    columns: {
      no: 'No',
      description: 'Task Description',
      department: 'Department',
      priority: 'Priority',
      time: 'Planned Time',
      responsible: 'Responsible',
      status: 'Status',
      action: 'Action',
    },
    actionsFor: 'Actions for {{task}}',
    view: 'View details',
    edit: 'Edit',
    start: 'Start',
    complete: 'Complete',
    cancel: 'Cancel task',
    remove: 'Remove',
    emptyTitle: 'No tasks planned for this day',
    emptyText: 'Add the works of the day with "New Plan".',
    emptyOffice: 'No tasks were planned on board for this day.',
    noMatch: 'No task matches the search.',
    loadError: 'The daily works plan could not be loaded.',
    retry: 'Retry',
    previousDay: 'Previous day',
    nextDay: 'Next day',
    newPlan: 'New Plan',
    pmsLinked: 'Linked to PMS work order {{no}}',
    permit: 'Permit required',
  },
  summary: {
    title: "Today's Summary",
    titleDay: 'Summary of the Day',
    completed: 'Completed',
    inProgress: 'In Progress',
    planned: 'Planned',
    overdue: 'Overdue',
    ring: '{{done}}/{{total}}',
    ringLabel: 'Completed',
    ringAria: '{{done}} of {{total}} tasks completed',
    byDepartment: 'By Department',
    noTasks: 'No tasks',
  },
  quick: {
    title: 'Quick Actions',
    addWork: 'Add Daily Work',
    generateReport: 'Generate Report',
    history: 'View Report History',
    exportPdf: 'Export PDF',
  },
  recent: {
    title: 'Recent Reports',
    viewAll: 'View All',
    date: 'Date',
    type: 'Type',
    status: 'Status',
    submittedBy: 'Submitted By',
    empty: 'No reports in the last 7 days.',
  },
  chart: {
    title: 'Reports by Type',
    last7: '(Last 7 Days)',
    aria: 'Reports by type in the last 7 days',
    none: 'No reports in the last 7 days.',
  },
  task: {
    newTitle: 'New Task',
    editTitle: 'Edit Task',
    day: 'Date',
    start: 'Start',
    end: 'End',
    department: 'Department',
    description: 'Work Description',
    responsible: 'Responsible',
    responsiblePlaceholder: 'e.g. Chief Engineer',
    priority: 'Priority',
    workOrder: 'PMS Work Order',
    workOrderPlaceholder: 'Optional',
    permit: 'Permit Required',
    risk: 'Risk Assessment',
    spares: 'Required Spares / Tools',
    save: 'Save Task',
    cancel: 'Cancel',
    completeTitle: 'Complete Task',
    completeText:
      'Write what was done. The note is kept with the task and cannot be changed later.',
    note: 'Notes',
    completeConfirm: 'Mark Completed',
    cancelTitle: 'Cancel Task',
    cancelText: 'A cancelled task stays in the plan with its reason.',
    reason: 'Reason',
    cancelConfirm: 'Cancel Task',
    removeTitle: 'Remove Task',
    removeText:
      'Remove "{{task}}" from the plan? Only planned tasks can be removed; the change is kept in the audit trail.',
    removeConfirm: 'Remove',
    back: 'Back',
  },
  details: {
    title: 'Task Details',
    close: 'Close',
    dateTime: 'Date / Time',
    responsible: 'Responsible',
    priority: 'Priority',
    status: 'Status',
    permit: 'Permit Required',
    risk: 'Risk Assessment',
    spares: 'Required Spares / Tools',
    pms: 'PMS Reference',
    description: 'Work Description',
    notes: 'Notes',
    cancelReason: 'Cancellation Reason',
    yes: 'Yes',
    no: 'No',
    none: '—',
    viewPms: 'View PMS Record',
    completedAt: 'Completed {{at}}',
  },
  reports: {
    title: 'Daily Reports',
    description: 'Record daily activities, operational status and observations.',
    descriptionOffice:
      'Daily reports are written and submitted on board. The office sees submitted reports.',
    allTypes: 'All Report Types',
    typeFilter: 'Report type',
    search: 'Search in reports...',
    searchLabel: 'Search reports',
    newReport: 'New Report',
    newNoon: 'Noon Report (Voyage)',
    listTitle: 'Daily Reports List',
    columns: {
      dateTime: 'Date / Time',
      type: 'Report Type',
      summary: 'Summary',
      status: 'Status',
      submittedBy: 'Submitted By',
      actions: 'Actions',
    },
    sortBy: 'Sort by {{column}}',
    view: 'View {{report}}',
    pagination: 'Report pages',
    pager: {
      previous: 'Previous page',
      next: 'Next page',
      first: 'First page',
      last: 'Last page',
    },
    emptyTitle: 'No reports for this day',
    emptyText: 'Create a report with "New Report".',
    emptyOffice: 'No reports were submitted for this day.',
    noMatch: 'No report matches the filter.',
    loadError: 'The daily reports could not be loaded.',
    retry: 'Retry',
    card: {
      none: 'No report',
      openItems_one: '{{count}} open item',
      openItems_other: '{{count}} open items',
      noVoyage: 'No noon report for this day',
      open: 'Open {{kind}}',
    },
    noonHint: 'Noon reports are kept in Voyage › Noon Report.',
    selectHint: 'Select a report to see its details.',
  },
  form: {
    title: 'Daily Report Entry',
    editTitle: 'Edit {{kind}}',
    tabs: {
      general: 'General',
      deck: 'Deck',
      engine: 'Engine',
      safety: 'Safety',
      environment: 'Environment',
      remarks: 'Remarks',
    },
    reportDate: 'Report Date',
    vessel: 'Vessel',
    reportType: 'Report Type',
    department: 'Department',
    departmentAll: 'All Departments',
    weather: 'Weather Condition',
    sea: 'Sea Condition',
    position: 'Port / Location',
    positionPlaceholder: 'e.g. At Sea, Fethiye Bay',
    nextPort: 'Next Port',
    summary: 'Summary',
    counter: '{{count}}/{{max}}',
    deck: 'Deck',
    engine: 'Engine',
    safety: 'Safety',
    environment: 'Environment',
    remarks: 'Remarks',
    port: 'Port',
    berth: 'Berth / Anchorage',
    activities: 'Port Activities',
    category: 'Category',
    location: 'Location',
    assignedTo: 'Assigned To',
    deadline: 'Deadline',
    actionTaken: 'Action Taken',
    closed: 'Closed',
    topic: 'Topic',
    result: 'Result',
    nextDue: 'Next Due',
    hseHint:
      'A non-conformity, deficiency or near miss is reported in Compliance; an equipment failure is a PMS defect.',
    attachments: 'Attachments',
    attachHint: 'Save the draft to add attachments.',
    upload: 'Click to upload',
    uploadFormats: 'Supported formats: JPG, PNG, PDF (Max 25 MB)',
    uploading: 'Uploading…',
    save: 'Save Report',
    saveDraft: 'Save Draft',
    reset: 'Reset',
    cancel: 'Cancel',
    required: 'Required',
    shipTime: 'Ship time',
    invalid: 'Check the highlighted fields.',
  },
  detail: {
    close: 'Close',
    dateTime: 'Date / Time',
    department: 'Department',
    revision: 'Revision {{no}}',
    summary: 'Summary',
    fields: 'Details',
    attachments: 'Attachments',
    noAttachments: 'No attachments.',
    fileOnVessel: 'File on vessel',
    history: 'Revision History',
    historyRow: 'Rev. {{no}} · {{at}} · {{by}}',
    approvedBy: 'Approved by {{by}} ({{role}}) · {{at}}',
    returnedBy: 'Returned by {{by}} · {{at}}: {{reason}}',
    reopenedBy: 'Reopened by {{by}} · {{at}}: {{reason}}',
    awaiting: 'Awaiting approval: {{role}}',
    submittedBy: 'Submitted by {{by}} · {{at}}',
    returnedNote: 'Returned: {{reason}}',
    edit: 'Edit',
    submit: 'Submit',
    discard: 'Discard Draft',
    approve: 'Approve',
    return: 'Return',
    reopen: 'Reopen for Correction',
    lockedNote: 'Submitted reports cannot be changed. A correction is made with a new revision.',
    convertToDefect: 'Convert to Defect',
    convertedTo: 'Converted to defect {{no}}.',
    openDefect: 'Open defect',
    noonOpen: 'Open in Voyage',
    position: 'Position',
    weather: 'Weather',
    sea: 'Sea Condition',
    mainEngine: 'Main Engine',
    voyage: 'Voyage',
    windValue: 'Bf {{bf}}',
    seaValue: 'Sea state {{state}}',
    rpmValue: '{{rpm}} rpm',
  },
  dialogs: {
    password: 'Password',
    passwordHint: 'Enter your password to sign.',
    reason: 'Reason',
    cancel: 'Cancel',
    approveTitle: 'Approve Report',
    approveText: 'Approve {{kind}} of {{day}}. Your approval is bound to the submitted content.',
    returnTitle: 'Return Report',
    returnText: 'The report goes back to its author for correction.',
    reopenTitle: 'Reopen Report',
    reopenText:
      'The report will be corrected and submitted as a new revision. The submitted revision is kept.',
    discardTitle: 'Discard Draft',
    discardText: 'The draft is removed from the list; it stays in the audit trail with the reason.',
    submitTitle: 'Submit Report',
    submitText: 'Submitted reports cannot be changed.',
    submitReason: 'What was corrected',
    confirm: 'Confirm',
  },
  pdf: {
    title: 'Daily Works Plan',
    vessel: 'Vessel',
    day: 'Date',
    generated: 'Generated {{at}} by Felixmate',
  },
  errors: {
    generic: 'The action could not be completed.',
    TASK_CLOSED: 'Completed and cancelled tasks cannot be changed.',
    TASK_STARTED: 'A started task cannot be removed; cancel it instead.',
    COMPLETION_NOTE_REQUIRED: 'Write a note to complete the task.',
    REASON_REQUIRED: 'A reason is required.',
    FUTURE_DATE: 'The date cannot be in the future.',
    INVALID_TRANSITION: 'This status change is not allowed.',
    INVALID_DAILY_TASK: 'Check the task fields.',
    INVALID_DAILY_REPORT: 'Check the report fields.',
    DAILY_REPORT_INCOMPLETE: 'Fill in the required fields before submitting.',
    REPORT_SUBMITTED: 'Submitted reports cannot be changed.',
    WORK_ORDER_CANCELLED: 'The work order is cancelled.',
    INVALID_CREDENTIALS: 'The password is incorrect.',
    RETURN_REASON_REQUIRED: 'Write the reason for returning.',
    REOPEN_REASON_REQUIRED: 'Write the reason for reopening.',
    SELF_APPROVAL_FORBIDDEN: 'You cannot approve your own report.',
    NO_ELIGIBLE_APPROVER: 'No approver is on board today.',
    ROW_VERSION_CONFLICT: 'Someone else changed this record. Reload and try again.',
    FORBIDDEN: 'You are not allowed to do this.',
    STALE_DECISION: 'The report changed. Reload and try again.',
    UNSUPPORTED_FILE_TYPE: 'This file type is not supported.',
    FILE_TOO_LARGE: 'The file is too large.',
  },
};

export const dailyTr: typeof dailyEn = {
  departments: {
    deck: 'Güverte',
    engine: 'Makine',
    navigation: 'Seyir',
    safety: 'Emniyet',
    environment: 'Çevre',
    interior: 'İç hizmetler',
    admin: 'İdari',
    all: 'Tümü',
  },
  priorities: { low: 'Düşük', medium: 'Orta', high: 'Yüksek' },
  statuses: {
    planned: 'Planlandı',
    in_progress: 'Devam ediyor',
    completed: 'Tamamlandı',
    cancelled: 'İptal edildi',
    overdue: 'Gecikmiş',
  },
  kinds: {
    noon: 'Öğle Raporu',
    daily: 'Günlük Rapor',
    port: 'Liman Raporu',
    defect_observation: 'Arıza / Gözlem',
    safety: 'Emniyet Raporu',
  },
  reportStatuses: {
    draft: 'Taslak',
    submitted: 'Gönderildi',
    approved: 'Onaylandı',
    final: 'Gönderildi',
    returned: 'İade edildi',
    reopened: 'Yeniden açıldı',
    open: 'Açık',
    closed: 'Kapandı',
  },
  weather: { good: 'İyi', fair: 'Fena değil', moderate: 'Orta', poor: 'Kötü', bad: 'Çok kötü' },
  sea: {
    calm: 'Sakin',
    slight: 'Hafif dalgalı',
    moderate: 'Orta dalgalı',
    rough: 'Dalgalı',
    very_rough: 'Çok dalgalı',
  },
  categories: { defect: 'Arıza', observation: 'Gözlem' },
  results: { passed: 'Başarılı', partial: 'Kısmen başarılı', failed: 'Başarısız' },
  head: {
    region: 'Gemi ve tarih',
    vesselType: 'Gemi Tipi',
    flag: 'Bayrak',
    date: 'Tarih',
    imo: 'IMO {{imo}}',
    vessel: 'Gemi',
  },
  plan: {
    title: 'Günlük İş Planı',
    description: 'Günlük işleri planlayın ve sorumluları atayın.',
    descriptionOffice: 'Günlük işler gemide planlanır. Ofis her günün planını görür.',
    all: 'Tümü',
    tabs: 'Departmanlar',
    search: 'Görev ara...',
    searchLabel: 'Görevlerde ara',
    columns: {
      no: 'No',
      description: 'Görev',
      department: 'Departman',
      priority: 'Öncelik',
      time: 'Planlanan Saat',
      responsible: 'Sorumlu',
      status: 'Durum',
      action: 'İşlem',
    },
    actionsFor: '{{task}} için işlemler',
    view: 'Ayrıntıları gör',
    edit: 'Düzenle',
    start: 'Başlat',
    complete: 'Tamamla',
    cancel: 'Görevi iptal et',
    remove: 'Kaldır',
    emptyTitle: 'Bu gün için planlanmış görev yok',
    emptyText: 'Günün işlerini "Yeni Plan" ile ekleyin.',
    emptyOffice: 'Bu gün için gemide görev planlanmamış.',
    noMatch: 'Aramaya uyan görev yok.',
    loadError: 'Günlük iş planı yüklenemedi.',
    retry: 'Yeniden dene',
    previousDay: 'Önceki gün',
    nextDay: 'Sonraki gün',
    newPlan: 'Yeni Plan',
    pmsLinked: 'PMS iş emri {{no}} ile bağlı',
    permit: 'İzin gerekli',
  },
  summary: {
    title: 'Bugünün Özeti',
    titleDay: 'Günün Özeti',
    completed: 'Tamamlandı',
    inProgress: 'Devam ediyor',
    planned: 'Planlandı',
    overdue: 'Gecikmiş',
    ring: '{{done}}/{{total}}',
    ringLabel: 'Tamamlandı',
    ringAria: '{{total}} görevin {{done}} tanesi tamamlandı',
    byDepartment: 'Departmana Göre',
    noTasks: 'Görev yok',
  },
  quick: {
    title: 'Hızlı İşlemler',
    addWork: 'Günlük İş Ekle',
    generateReport: 'Rapor Oluştur',
    history: 'Rapor Geçmişi',
    exportPdf: 'PDF Dışa Aktar',
  },
  recent: {
    title: 'Son Raporlar',
    viewAll: 'Tümünü Gör',
    date: 'Tarih',
    type: 'Tür',
    status: 'Durum',
    submittedBy: 'Gönderen',
    empty: 'Son 7 günde rapor yok.',
  },
  chart: {
    title: 'Türe Göre Raporlar',
    last7: '(Son 7 Gün)',
    aria: 'Son 7 günde türe göre raporlar',
    none: 'Son 7 günde rapor yok.',
  },
  task: {
    newTitle: 'Yeni Görev',
    editTitle: 'Görevi Düzenle',
    day: 'Tarih',
    start: 'Başlangıç',
    end: 'Bitiş',
    department: 'Departman',
    description: 'Yapılacak İş',
    responsible: 'Sorumlu',
    responsiblePlaceholder: 'ör. Başmühendis',
    priority: 'Öncelik',
    workOrder: 'PMS İş Emri',
    workOrderPlaceholder: 'İsteğe bağlı',
    permit: 'İzin Gerekli',
    risk: 'Risk Değerlendirmesi',
    spares: 'Gereken Yedek Parça / Araç',
    save: 'Görevi Kaydet',
    cancel: 'Vazgeç',
    completeTitle: 'Görevi Tamamla',
    completeText: 'Yapılanı yazın. Not görevle birlikte saklanır, sonradan değiştirilemez.',
    note: 'Notlar',
    completeConfirm: 'Tamamlandı İşaretle',
    cancelTitle: 'Görevi İptal Et',
    cancelText: 'İptal edilen görev gerekçesiyle planda kalır.',
    reason: 'Gerekçe',
    cancelConfirm: 'Görevi İptal Et',
    removeTitle: 'Görevi Kaldır',
    removeText:
      '"{{task}}" plandan kaldırılsın mı? Yalnız planlı görev kaldırılır; değişiklik denetim kaydında kalır.',
    removeConfirm: 'Kaldır',
    back: 'Geri',
  },
  details: {
    title: 'Görev Ayrıntısı',
    close: 'Kapat',
    dateTime: 'Tarih / Saat',
    responsible: 'Sorumlu',
    priority: 'Öncelik',
    status: 'Durum',
    permit: 'İzin Gerekli',
    risk: 'Risk Değerlendirmesi',
    spares: 'Gereken Yedek Parça / Araç',
    pms: 'PMS Referansı',
    description: 'Yapılacak İş',
    notes: 'Notlar',
    cancelReason: 'İptal Gerekçesi',
    yes: 'Evet',
    no: 'Hayır',
    none: '—',
    viewPms: 'PMS Kaydını Gör',
    completedAt: '{{at}} tamamlandı',
  },
  reports: {
    title: 'Günlük Raporlar',
    description: 'Günlük faaliyetleri, operasyon durumunu ve gözlemleri kaydedin.',
    descriptionOffice:
      'Günlük raporlar gemide yazılır ve gönderilir. Ofis gönderilen raporları görür.',
    allTypes: 'Bütün Rapor Türleri',
    typeFilter: 'Rapor türü',
    search: 'Raporlarda ara...',
    searchLabel: 'Raporlarda ara',
    newReport: 'Yeni Rapor',
    newNoon: 'Öğle Raporu (Sefer)',
    listTitle: 'Günlük Rapor Listesi',
    columns: {
      dateTime: 'Tarih / Saat',
      type: 'Rapor Türü',
      summary: 'Özet',
      status: 'Durum',
      submittedBy: 'Gönderen',
      actions: 'İşlemler',
    },
    sortBy: '{{column}} sütununa göre sırala',
    view: '{{report}} görüntüle',
    pagination: 'Rapor sayfaları',
    pager: {
      previous: 'Önceki sayfa',
      next: 'Sonraki sayfa',
      first: 'İlk sayfa',
      last: 'Son sayfa',
    },
    emptyTitle: 'Bu gün için rapor yok',
    emptyText: '"Yeni Rapor" ile rapor oluşturun.',
    emptyOffice: 'Bu gün için gönderilmiş rapor yok.',
    noMatch: 'Süzgece uyan rapor yok.',
    loadError: 'Günlük raporlar yüklenemedi.',
    retry: 'Yeniden dene',
    card: {
      none: 'Rapor yok',
      openItems_one: '{{count}} açık kalem',
      openItems_other: '{{count}} açık kalem',
      noVoyage: 'Bu gün için öğle raporu yok',
      open: '{{kind}} aç',
    },
    noonHint: 'Öğle raporları Sefer › Öğle Raporu altında tutulur.',
    selectHint: 'Ayrıntısını görmek için bir rapor seçin.',
  },
  form: {
    title: 'Günlük Rapor Girişi',
    editTitle: '{{kind}} düzenle',
    tabs: {
      general: 'Genel',
      deck: 'Güverte',
      engine: 'Makine',
      safety: 'Emniyet',
      environment: 'Çevre',
      remarks: 'Notlar',
    },
    reportDate: 'Rapor Tarihi',
    vessel: 'Gemi',
    reportType: 'Rapor Türü',
    department: 'Departman',
    departmentAll: 'Bütün Departmanlar',
    weather: 'Hava Durumu',
    sea: 'Deniz Durumu',
    position: 'Liman / Konum',
    positionPlaceholder: 'ör. Denizde, Fethiye Körfezi',
    nextPort: 'Sonraki Liman',
    summary: 'Özet',
    counter: '{{count}}/{{max}}',
    deck: 'Güverte',
    engine: 'Makine',
    safety: 'Emniyet',
    environment: 'Çevre',
    remarks: 'Notlar',
    port: 'Liman',
    berth: 'Rıhtım / Demir Yeri',
    activities: 'Liman Faaliyetleri',
    category: 'Kategori',
    location: 'Yer',
    assignedTo: 'Atanan',
    deadline: 'Son Tarih',
    actionTaken: 'Yapılan İşlem',
    closed: 'Kapandı',
    topic: 'Konu',
    result: 'Sonuç',
    nextDue: 'Sonraki Tarih',
    hseHint:
      'Uygunsuzluk, eksiklik ya da ramak kala Compliance menüsünde raporlanır; ekipman arızası PMS arıza kaydıdır.',
    attachments: 'Ekler',
    attachHint: 'Ek eklemek için taslağı kaydedin.',
    upload: 'Yüklemek için tıklayın',
    uploadFormats: 'Desteklenen biçimler: JPG, PNG, PDF (en çok 25 MB)',
    uploading: 'Yükleniyor…',
    save: 'Raporu Kaydet',
    saveDraft: 'Taslağı Kaydet',
    reset: 'Sıfırla',
    cancel: 'Vazgeç',
    required: 'Zorunlu',
    shipTime: 'Gemi saati',
    invalid: 'İşaretli alanları kontrol edin.',
  },
  detail: {
    close: 'Kapat',
    dateTime: 'Tarih / Saat',
    department: 'Departman',
    revision: 'Revizyon {{no}}',
    summary: 'Özet',
    fields: 'Ayrıntılar',
    attachments: 'Ekler',
    noAttachments: 'Ek yok.',
    fileOnVessel: 'Dosya gemide',
    history: 'Revizyon Geçmişi',
    historyRow: 'Rev. {{no}} · {{at}} · {{by}}',
    approvedBy: '{{by}} ({{role}}) onayladı · {{at}}',
    returnedBy: '{{by}} iade etti · {{at}}: {{reason}}',
    reopenedBy: '{{by}} yeniden açtı · {{at}}: {{reason}}',
    awaiting: 'Onay bekliyor: {{role}}',
    submittedBy: '{{by}} gönderdi · {{at}}',
    returnedNote: 'İade: {{reason}}',
    edit: 'Düzenle',
    submit: 'Gönder',
    discard: 'Taslaktan Vazgeç',
    approve: 'Onayla',
    return: 'İade Et',
    reopen: 'Düzeltme İçin Yeniden Aç',
    lockedNote: 'Gönderilen rapor değiştirilemez. Düzeltme yeni revizyonla yapılır.',
    convertToDefect: 'Arızaya Dönüştür',
    convertedTo: '{{no}} arızasına dönüştürüldü.',
    openDefect: 'Arızayı aç',
    noonOpen: 'Seferde Aç',
    position: 'Konum',
    weather: 'Hava',
    sea: 'Deniz Durumu',
    mainEngine: 'Ana Makine',
    voyage: 'Sefer',
    windValue: 'Bf {{bf}}',
    seaValue: 'Deniz durumu {{state}}',
    rpmValue: '{{rpm}} dev/dk',
  },
  dialogs: {
    password: 'Parola',
    passwordHint: 'İmzalamak için parolanızı girin.',
    reason: 'Gerekçe',
    cancel: 'Vazgeç',
    approveTitle: 'Raporu Onayla',
    approveText: '{{day}} tarihli {{kind}} onaylanacak. Onayınız gönderilen içeriğe bağlıdır.',
    returnTitle: 'Raporu İade Et',
    returnText: 'Rapor düzeltilmek üzere yazanına döner.',
    reopenTitle: 'Raporu Yeniden Aç',
    reopenText: 'Rapor düzeltilip yeni revizyonla gönderilecek. Gönderilen revizyon saklanır.',
    discardTitle: 'Taslaktan Vazgeç',
    discardText: 'Taslak listeden kalkar; gerekçesiyle denetim kaydında kalır.',
    submitTitle: 'Raporu Gönder',
    submitText: 'Gönderilen rapor değiştirilemez.',
    submitReason: 'Neler düzeltildi',
    confirm: 'Onayla',
  },
  pdf: {
    title: 'Günlük İş Planı',
    vessel: 'Gemi',
    day: 'Tarih',
    generated: '{{at}} tarihinde Felixmate ile oluşturuldu',
  },
  errors: {
    generic: 'İşlem tamamlanamadı.',
    TASK_CLOSED: 'Tamamlanan ve iptal edilen görev değiştirilemez.',
    TASK_STARTED: 'Başlamış görev kaldırılamaz; iptal edin.',
    COMPLETION_NOTE_REQUIRED: 'Görevi tamamlamak için not yazın.',
    REASON_REQUIRED: 'Gerekçe zorunludur.',
    FUTURE_DATE: 'Tarih gelecekte olamaz.',
    INVALID_TRANSITION: 'Bu durum değişikliğine izin verilmez.',
    INVALID_DAILY_TASK: 'Görev alanlarını kontrol edin.',
    INVALID_DAILY_REPORT: 'Rapor alanlarını kontrol edin.',
    DAILY_REPORT_INCOMPLETE: 'Göndermeden önce zorunlu alanları doldurun.',
    REPORT_SUBMITTED: 'Gönderilen rapor değiştirilemez.',
    WORK_ORDER_CANCELLED: 'İş emri iptal edilmiş.',
    INVALID_CREDENTIALS: 'Parola yanlış.',
    RETURN_REASON_REQUIRED: 'İade gerekçesini yazın.',
    REOPEN_REASON_REQUIRED: 'Yeniden açma gerekçesini yazın.',
    SELF_APPROVAL_FORBIDDEN: 'Kendi raporunuzu onaylayamazsınız.',
    NO_ELIGIBLE_APPROVER: 'Bugün gemide onaycı yok.',
    ROW_VERSION_CONFLICT: 'Kayıt başkası tarafından değiştirildi. Yenileyip tekrar deneyin.',
    FORBIDDEN: 'Bu işlem için yetkiniz yok.',
    STALE_DECISION: 'Rapor değişti. Yenileyip tekrar deneyin.',
    UNSUPPORTED_FILE_TYPE: 'Bu dosya türü desteklenmiyor.',
    FILE_TOO_LARGE: 'Dosya çok büyük.',
  },
};
